import { browserTracingIntegration } from '@sentry/browser';
import * as Sentry from '@sentry/react';
import { pick } from 'ramda';
import { env, isProd, isStaging, SENTRY_DERIVED_DSN, sentryClientSampleRate } from 'src/config';
import type { Apollo } from 'src/gql/generated';

import { useCurrentUserQuery } from './gql';

if (isProd || isStaging) {
  Sentry.init({
    attachStacktrace: true,
    dsn: SENTRY_DERIVED_DSN,
    environment: env,
    integrations: [browserTracingIntegration()],
    sampleRate: sentryClientSampleRate,
  });
}

type User = Pick<Apollo.User, 'id' | 'displayName' | 'email'>;
const onlyUserAttrs = pick(['id', 'email', 'displayName']);

/** Includes current user information for Sentry logging. */
function setSentryUser(user: User): void {
  Sentry.setUser({ ...onlyUserAttrs(user), username: user.displayName });
}

/** Wipe current user from Sentry information */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

export function useSetSentryUser(): ReturnType<typeof useCurrentUserQuery> {
  const currentUserRes = useCurrentUserQuery();
  if (currentUserRes.currentUser) setSentryUser(currentUserRes.currentUser);

  return currentUserRes;
}

export { Sentry };
